import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ImageViewer from '../UI/ImageViewer/ImageViewer';
import ArtworkCard from '../ArtworkCard/ArtworkCard';
import VerifiedBadge from '../UI/VerifiedBadge/VerifiedBadge';
import './ProfileView.v2.css';

// Artworks Section Component
const ArtworksSection = ({ username, isPreview }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [artworks, setArtworks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!username) {
            setLoading(false);
            return;
        }

        const fetchArtworks = async () => {
            try {
                const res = await api.get(`/artworks/user/${username}?page=1&limit=3`);
                setArtworks(res.data.artworks || []);
            } catch (err) {
                console.error('Error fetching artworks:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchArtworks();
    }, [username, isPreview]);

    if (loading || artworks.length === 0) {
        return null;
    }

    return (
        <section className="section-container">
            <h3 className="section-title">
                {t('view_all_artworks')}
            </h3>

            <div className="gallery-grid">
                {artworks.map((artwork) => (
                    <ArtworkCard
                        key={artwork.id}
                        artwork={artwork}
                        onClick={() => !isPreview && navigate(`/artworks/${artwork.id}`)}
                    />
                ))}
            </div>

            {!isPreview && (
                <div style={{ textAlign: 'center', marginTop: '40px' }}>
                    <button
                        onClick={() => navigate(`/profile/${username}/artworks`)}
                        className="cta-button"
                        style={{ background: 'transparent', color: 'var(--profile-accent)', border: '2px solid' }}
                    >
                        {t('view_all_artworks')} →
                    </button>
                </div>
            )}
        </section>
    );
};

const ProfileView = ({ data, username, isPreview = false, onRequestQuote }) => {
    const { t } = useTranslation();
    const [selectedImage, setSelectedImage] = useState(null);

    if (!data) return null;

    const bgSettings = data.backgroundSettings || { type: 'color', value: '#0a0a0a' };
    const hasBgImage = bgSettings.type === 'image';

    const wrapperStyle = !hasBgImage ? { backgroundColor: bgSettings.value } : {};
    const bgImageStyle = hasBgImage ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        backgroundImage: `url(${bgSettings.value})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: 0,
        pointerEvents: 'none',
        backgroundAttachment: 'fixed'
    } : null;

    return (
        <div className="profile-view-wrapper" style={wrapperStyle}>
            {hasBgImage && <div className="profile-fixed-bg" style={bgImageStyle} />}
            <div className="profile-overlay">

                {/* Cover Photo Section */}
                <div
                    className="profile-cover"
                    style={{ backgroundImage: data.coverPhoto ? `url(${data.coverPhoto})` : 'none' }}
                >
                </div>

                {/* Profile Header Content */}
                <div className="profile-header-content">
                    {/* Profile Picture */}
                    <div className="profile-picture-container">
                        {data.profilePicture ? (
                            <img src={data.profilePicture} alt={data.artistName || t('artist_fallback')} />
                        ) : (
                            <div className="profile-avatar-fallback">
                                <span>
                                    {data.artistName
                                        ? data.artistName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                                        : (username ? username.substring(0, 2).toUpperCase() : '?')}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Artist Info */}
                    <div className="artist-main-info">
                        <h1 className="artist-name">
                            {data.artistName || (username ? `@${username}` : t('artist_fallback'))}
                            {data.isVerified && <VerifiedBadge size={22} />}
                        </h1>

                        <div className="artist-sub-info">
                            {data.studio && (
                                <span className="info-item">🏢 {data.studio}</span>
                            )}
                            {data.studio && <span className="info-separator">|</span>}
                            <span className="info-item">📍 {data.location || 'Brasil'}</span>
                        </div>

                        {/* Specialty Styles Badges */}
                        {data.specialtyStylesData && data.specialtyStylesData.length > 0 && (
                            <div className="specialty-badges">
                                {data.specialtyStylesData.map(style => (
                                    <span key={style.id} className="style-badge">
                                        {t(`style_${style.name.toLowerCase().replace(/\s+/g, '_')}`) || style.name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Actions & Socials */}
                    <div className="profile-actions">
                        {onRequestQuote && (
                            <button
                                className="cta-button cta-header"
                                onClick={isPreview ? null : onRequestQuote}
                                style={isPreview ? { opacity: 0.7, cursor: 'default' } : {}}
                            >
                                {t('request_quote') || 'Solicitar uma Arte'}
                            </button>
                        )}

                        <div className="social-links">
                            {data.whatsapp && (
                                <a
                                    href={`https://wa.me/${data.whatsapp}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-icon whatsapp"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                    </svg>
                                </a>
                            )}

                            {data.contactEmail && (
                                <a
                                    href={`mailto:${data.contactEmail}`}
                                    className="social-icon"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                                    </svg>
                                </a>
                            )}

                            {data.instagram && (
                                <a
                                    href={data.instagram.includes('http') ? data.instagram : `https://instagram.com/${data.instagram.replace('@', '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-icon instagram"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                    </svg>
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bio Section */}
                {data.bio && (
                    <div className="profile-bio-container">
                        <p>{data.bio}</p>
                    </div>
                )}

                {/* Video Section */}
                {data.videoUrl && (
                    <div className="video-showcase">
                        {(data.videoUrl.includes('youtube.com') || data.videoUrl.includes('youtu.be')) ? (
                            <iframe
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                src={`https://www.youtube.com/embed/${data.videoUrl.includes('v=') ? data.videoUrl.split('v=')[1].split('&')[0] : data.videoUrl.split('/').pop()}?autoplay=1&mute=1&controls=0&loop=1&playlist=${data.videoUrl.includes('v=') ? data.videoUrl.split('v=')[1].split('&')[0] : data.videoUrl.split('/').pop()}`}
                                title={t('presentation_video')}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            ></iframe>
                        ) : data.videoUrl.includes('vimeo.com') ? (
                            <iframe
                                src={`https://player.vimeo.com/video/${data.videoUrl.split('/').pop()}?autoplay=1&muted=1&loop=1&background=1`}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                title={t('presentation_video')}
                                allow="autoplay; fullscreen; picture-in-picture"
                            ></iframe>
                        ) : (
                            <video
                                src={data.videoUrl}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                autoPlay
                                muted
                                loop
                                playsInline
                            />
                        )}
                    </div>
                )}

                {/* Portfolio Grid */}
                <section className="section-container">
                    <h3 className="section-title">
                        {t('gallery_title')}
                    </h3>

                    {(!data.portfolio || data.portfolio.length === 0) ? (
                        <p style={{ color: '#888', textAlign: 'center', fontStyle: 'italic' }}>{t('empty_gallery')}</p>
                    ) : (
                        <div className="gallery-grid">
                            {data.portfolio.slice(0, 6).map((img, idx) => (
                                <div
                                    key={idx}
                                    className="gallery-item"
                                    onClick={() => setSelectedImage(img)}
                                >
                                    <img src={img} alt={`Art ${idx}`} />
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Artworks Section */}
                <ArtworksSection username={username} isPreview={isPreview} />

                {/* Footer */}
                <footer style={{ padding: '80px 0 100px', textAlign: 'center', opacity: 0.4 }}>
                    <p style={{ fontSize: '0.9rem', letterSpacing: '1px' }} dangerouslySetInnerHTML={{ __html: t('footer_copyright') }}></p>
                </footer>
            </div>

            {/* Mobile Sticky CTA */}
            {onRequestQuote && (
                <div className="mobile-sticky-cta" style={isPreview ? { position: 'absolute', bottom: '20px' } : {}}>
                    <button
                        className="cta-button"
                        onClick={isPreview ? null : onRequestQuote}
                        style={isPreview ? { opacity: 0.7, cursor: 'default' } : {}}
                    >
                        {t('request_quote') || 'Solicitar uma Arte'}
                    </button>
                </div>
            )}

            <ImageViewer
                src={selectedImage}
                onClose={() => setSelectedImage(null)}
            />
        </div>
    );
};

export default ProfileView;
